import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ClipboardCheck, Plus, X, CheckCircle2, Play, AlertTriangle, Camera,
  MessageSquare, Trash2, Edit3, Clock, ChevronRight, Filter, Loader2, Image as ImageIcon, Bell, BellOff, Moon, Settings, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { compressImageToBase64 } from '../utils/imageCompress';
import { enablePushNotifications, pushSupported, pushPermission, isFirebaseConfigured } from '../utils/firebase';
import { resetAllServiceWorkers } from '../utils/pwa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS = [
  { value: 'limpeza', label: 'Limpeza de box', color: 'bg-blue-100 text-blue-700' },
  { value: 'medicacao', label: 'Medicação', color: 'bg-red-100 text-red-700' },
  { value: 'passeio', label: 'Passeio', color: 'bg-green-100 text-green-700' },
  { value: 'banho', label: 'Banho', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'alimentacao', label: 'Alimentação', color: 'bg-amber-100 text-amber-700' },
  { value: 'comportamento', label: 'Comportamento', color: 'bg-purple-100 text-purple-700' },
  { value: 'mover_box', label: 'Mover de box', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'outro', label: 'Outro', color: 'bg-gray-100 text-gray-700' },
];

const PRIORIDADES = [
  { value: 'baixa', label: 'Baixa', color: 'text-gray-600 bg-gray-100' },
  { value: 'normal', label: 'Normal', color: 'text-blue-700 bg-blue-100' },
  { value: 'alta', label: 'Alta', color: 'text-red-700 bg-red-100' },
];

const ESTADOS = [
  { value: 'pendente', label: 'Pendente', color: 'bg-amber-100 text-amber-700' },
  { value: 'em_curso', label: 'Em curso', color: 'bg-blue-100 text-blue-700' },
  { value: 'concluida', label: 'Concluída', color: 'bg-green-100 text-green-700' },
  { value: 'cancelada', label: 'Cancelada', color: 'bg-gray-200 text-gray-700' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};
const tipoLabel = (t) => TIPOS.find(x => x.value === t)?.label || t;
const tipoColor = (t) => TIPOS.find(x => x.value === t)?.color || 'bg-gray-100 text-gray-700';
const prioCfg = (p) => PRIORIDADES.find(x => x.value === p) || PRIORIDADES[1];
const estadoCfg = (e) => ESTADOS.find(x => x.value === e) || ESTADOS[0];

export default function TarefasPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isOp = user?.role === 'operacional';
  const canManage = user?.role === 'tecnico' || user?.role === 'admin';

  const [tarefas, setTarefas] = useState([]);
  const [contadores, setContadores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [filtroEstado, setFiltroEstado] = useState('pendente');
  const [filtroMinhas, setFiltroMinhas] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(params.get('id') || null);

  // Carregamento — refazer sempre que filtros mudam OU a cada 20s (polling)
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const q = new URLSearchParams();
        if (filtroEstado !== 'todas') q.set('estado', filtroEstado);
        if (filtroMinhas && canManage) q.set('minhas', 'true');
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/tarefas?${q.toString()}`),
          axios.get(`${API}/tarefas/contadores`),
        ]);
        if (cancelled) return;
        setTarefas(r1.data);
        setContadores(r2.data);
      } catch (e) { /* ignore polling errors */ }
      finally { if (!cancelled) setLoading(false); }
    };
    setLoading(true);
    fetchData();
    // Poll every 20s
    const iv = setInterval(fetchData, 20000);
    // Refresh immediately on tab focus (handles "minimize → reopen" case)
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    // Refresh immediately when an FCM push arrives in foreground
    const onPush = () => fetchData();
    window.addEventListener('animalog:push', onPush);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('animalog:push', onPush);
    };
  }, [filtroEstado, filtroMinhas, canManage, reloadKey]);

  const carregar = () => setReloadKey(k => k + 1);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-primary" />
            Tarefas
          </h1>
          <p className="text-sm text-muted-foreground">
            {isOp ? 'As tuas tarefas atribuídas' : 'Atribuir e acompanhar tarefas'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PushNotificationsButton />
          <button data-testid="btn-prefs-notif" onClick={() => setShowPrefs(true)} title="Definições de notificações"
            className="flex items-center gap-1.5 bg-muted text-foreground rounded-full px-3 py-1.5 text-xs font-medium hover:bg-muted/80 transition-colors">
            <Moon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Não-perturbar</span>
          </button>
          {canManage && (
            <button data-testid="btn-nova-tarefa" onClick={() => { setEditing(null); setShowForm(true); }}
              className="flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 transition-all">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nova tarefa</span>
            </button>
          )}
        </div>
      </div>

      {/* Cards de contadores */}
      {contadores && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ContadorCard label="Pendentes" value={contadores.pendente} active={filtroEstado === 'pendente'} onClick={() => setFiltroEstado('pendente')} color="bg-amber-50 text-amber-800 border-amber-200" />
          <ContadorCard label="Em curso" value={contadores.em_curso} active={filtroEstado === 'em_curso'} onClick={() => setFiltroEstado('em_curso')} color="bg-blue-50 text-blue-800 border-blue-200" />
          <ContadorCard label="Concluídas" value={contadores.concluida} active={filtroEstado === 'concluida'} onClick={() => setFiltroEstado('concluida')} color="bg-green-50 text-green-800 border-green-200" />
          <ContadorCard label="Atrasadas" value={contadores.atrasadas} active={false} onClick={() => setFiltroEstado('pendente')} color="bg-red-50 text-red-800 border-red-200" alert={contadores.atrasadas > 0} />
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {['todas', 'pendente', 'em_curso', 'concluida', 'cancelada'].map(e => (
          <button key={e} data-testid={`filtro-${e}`} onClick={() => setFiltroEstado(e)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${filtroEstado === e ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
            {e === 'todas' ? 'Todas' : estadoCfg(e).label}
          </button>
        ))}
        {canManage && (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={filtroMinhas} onChange={e => setFiltroMinhas(e.target.checked)} />
            Só as minhas
          </label>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : tarefas.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-2xl border border-dashed border-border">
          <ClipboardCheck className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Nenhuma tarefa encontrada com este filtro.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tarefas.map(t => (
            <TarefaCard key={t.id} t={t} onOpen={() => setDetailId(t.id)} />
          ))}
        </div>
      )}

      {/* Forms / detail */}
      {showForm && <FormTarefa initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); carregar(); }} />}
      {showPrefs && <PreferenciasModal onClose={() => setShowPrefs(false)} />}
      {detailId && (
        <DetalheTarefa
          tarefaId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={carregar}
          onEdit={(t) => { setEditing(t); setShowForm(true); setDetailId(null); }}
          canManage={canManage}
          isOp={isOp}
        />
      )}
    </div>
  );
}


function PushNotificationsButton() {
  const [state, setState] = useState({ supported: false, configured: false, permission: 'default' });
  // tokenOk: null = a verificar | true = token registado no servidor | false = falhou
  const [tokenOk, setTokenOk] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = await pushSupported();
      const configured = isFirebaseConfigured();
      const permission = pushPermission();
      if (!cancelled) setState({ supported, configured, permission });
      // If permission already granted, refresh token silently to keep backend up to date.
      // Result is tracked so the badge shows the REAL state (not just browser permission).
      if (supported && configured && permission === 'granted') {
        try {
          const res = await enablePushNotifications();
          if (!cancelled) {
            setTokenOk(!!res.ok);
            setTokenError(res.ok ? null : `${res.reason || 'erro'}${res.error ? ': ' + res.error : ''}`);
          }
        } catch (e) {
          if (!cancelled) { setTokenOk(false); setTokenError(String(e?.message || e)); }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state.supported || !state.configured) return null;
  if (state.permission === 'granted') {
    const sendTest = async () => {
      try {
        const r = await axios.post(`${API}/notificacoes/teste`);
        const m = r.data;
        if (m.tokens_registados === 0) {
          alert(`⚠️ Tens 0 tokens registados.\n\nFirebase no servidor: ${m.firebase_ready ? 'ON' : 'OFF'}\n\nClica em "🔄 Reparar" e ativa notificações outra vez.`);
        } else {
          const detalhes = (m.resultados || [])
            .map((x) => (x.ok ? `✅ ${x.token} enviado` : `❌ ${x.token} falhou: ${x.erro || '?'}`))
            .join('\n');
          alert(`Push de teste — resultado por dispositivo:\n\n${detalhes}\n\nTokens registados: ${m.tokens_registados} | Enviados: ${m.enviados_com_sucesso}\nFirebase: ${m.firebase_ready ? 'ON' : 'OFF'}\n\nSe diz "enviado" mas não aparece no telemóvel:\n• iOS: a app TEM de estar instalada no ecrã principal\n• Android: verifica as definições de notificações da app/site`);
        }
      } catch (e) {
        alert('Erro ao enviar push de teste: ' + (e.response?.data?.detail || e.message));
      }
    };
    const onReactivate = async () => {
      setBusy(true);
      const res = await enablePushNotifications();
      setBusy(false);
      setTokenOk(!!res.ok);
      if (res.ok) {
        setTokenError(null);
        alert('✅ Notificações reativadas — o token foi registado no servidor.');
      } else {
        const err = `${res.reason || 'erro'}${res.error ? ': ' + res.error : ''}`;
        setTokenError(err);
        alert(`❌ Continua a falhar o registo do token.\n\nErro: ${err}\n\nTenta o botão 🔄 Reparar (limpa cache/service workers) e volta a ativar.`);
      }
    };
    const onRepairGranted = async () => {
      if (!confirm('Isto vai limpar service workers e cache do Firebase para tentar de novo. Continuar?')) return;
      setBusy(true);
      await resetAllServiceWorkers();
      setTimeout(() => window.location.reload(), 400);
    };
    if (tokenOk === false) {
      return (
        <div className="flex items-center gap-1">
          <button data-testid="push-broken-indicator" disabled={busy} onClick={onReactivate}
            title={`O registo do token falhou: ${tokenError || 'erro desconhecido'}. Toca para tentar de novo.`}
            className="flex items-center gap-1.5 bg-orange-50 text-orange-700 border border-orange-300 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-orange-100 transition-colors disabled:opacity-50">
            <BellOff className="w-3.5 h-3.5" /> {busy ? 'A reativar…' : '⚠️ Reativar notificações'}
          </button>
          <button data-testid="repair-push-btn-granted" disabled={busy} onClick={onRepairGranted} title="Reparar notificações (limpa cache e tenta de novo)"
            className="flex items-center justify-center bg-muted text-muted-foreground rounded-full p-1.5 hover:bg-muted/80 transition-colors disabled:opacity-50">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1">
        <button data-testid="push-enabled-indicator" title={tokenOk ? 'Notificações ativas (token registado)' : 'A verificar o registo do token…'} disabled
          className="flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1.5 text-xs font-medium">
          <Bell className="w-3.5 h-3.5" /> {tokenOk === null ? 'A verificar…' : 'Notificações ativas'}
        </button>
        <button data-testid="test-push-btn" onClick={sendTest} title="Enviar uma notificação de teste para o meu dispositivo"
          className="bg-blue-50 text-blue-700 border border-blue-200 rounded-full p-1.5 hover:bg-blue-100 transition-colors">
          <Settings className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  if (state.permission === 'denied') {
    return (
      <span data-testid="push-denied-indicator" title="Notificações bloqueadas no browser. Ativa nas definições do site."
        className="flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 rounded-full px-3 py-1.5 text-xs font-medium">
        <BellOff className="w-3.5 h-3.5" /> Bloqueadas
      </span>
    );
  }
  const onEnable = async () => {
    setBusy(true);
    const res = await enablePushNotifications();
    setBusy(false);
    if (res.ok) {
      setState({ ...state, permission: 'granted' });
    } else if (res.reason === 'permission-denied') {
      setState({ ...state, permission: 'denied' });
      alert('Permissão de notificações negada. Podes mudar nas definições do site no browser.');
    } else {
      console.error('enablePushNotifications:', res);
      const reasonMap = {
        'unsupported': 'Este browser/dispositivo não suporta notificações push.',
        'not-configured': 'A configuração do Firebase está em falta.',
        'sw-register-failed': 'Não foi possível registar o service worker.',
        'messaging-init-failed': 'Não foi possível inicializar o Firebase Messaging.',
        'token-failed': 'O Firebase rejeitou o pedido de token. Verifica que estás em HTTPS e que o domínio está autorizado no Firebase Console.',
        'no-token': 'O Firebase devolveu um token vazio. A VAPID key pode estar errada.',
        'backend-register-failed': 'O servidor não conseguiu guardar o token.',
      };
      const human = reasonMap[res.reason] || `Não foi possível ativar notificações (${res.reason || 'erro desconhecido'}).`;
      alert(human + (res.error ? '\n\nDetalhes técnicos: ' + res.error : ''));
    }
  };
  const onRepair = async () => {
    if (!confirm('Isto vai limpar service workers e cache do Firebase para tentar de novo. Continuar?')) return;
    setBusy(true);
    await resetAllServiceWorkers();
    setTimeout(() => window.location.reload(), 400);
  };
  return (
    <div className="flex items-center gap-1">
      <button data-testid="enable-push-btn" disabled={busy} onClick={onEnable}
        className="flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-amber-100 transition-colors disabled:opacity-50">
        <Bell className="w-3.5 h-3.5" /> {busy ? 'A ativar…' : 'Ativar notificações'}
      </button>
      <button data-testid="repair-push-btn" disabled={busy} onClick={onRepair} title="Reparar notificações (limpa cache e tenta de novo)"
        className="flex items-center justify-center bg-muted text-muted-foreground rounded-full p-1.5 hover:bg-muted/80 transition-colors disabled:opacity-50">
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ContadorCard({ label, value, active, onClick, color, alert }) {
  return (
    <button data-testid={`card-${label}`} onClick={onClick}
      className={`text-left rounded-xl p-3 border transition-all ${color} ${active ? 'ring-2 ring-primary' : ''} ${alert ? 'animate-pulse' : ''}`}>
      <p className="text-xs uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-2xl font-bold">{value ?? 0}</p>
    </button>
  );
}

function TarefaCard({ t, onOpen }) {
  const estado = estadoCfg(t.estado);
  const prio = prioCfg(t.prioridade);
  const atrasada = t.prazo && new Date(t.prazo) < new Date() && t.estado !== 'concluida' && t.estado !== 'cancelada';
  return (
    <button data-testid={`tarefa-${t.id}`} onClick={onOpen}
      className="w-full bg-card border border-border rounded-2xl p-3 sm:p-4 hover:shadow-md transition-all text-left flex items-center gap-3 group">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${tipoColor(t.tipo)}`}>{tipoLabel(t.tipo)}</span>
          <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${estado.color}`}>{estado.label}</span>
          {t.prioridade === 'alta' && <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${prio.color} flex items-center gap-1`}><AlertTriangle className="w-3 h-3" />{prio.label}</span>}
          {atrasada && <span className="text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-700 flex items-center gap-1"><Clock className="w-3 h-3" />Atrasada</span>}
        </div>
        <h3 className="font-medium text-foreground truncate">{t.titulo}</h3>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          {t.box && <span>📍 {t.box}</span>}
          {t.animal_nome && <span>🐾 {t.animal_nome}</span>}
          <span>👤 {t.responsavel_nome}</span>
          {t.prazo && <span>⏰ {fmtDateTime(t.prazo)}</span>}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0" />
    </button>
  );
}

function FormTarefa({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    titulo: initial?.titulo || '',
    tipo: initial?.tipo || 'limpeza',
    descricao: initial?.descricao || '',
    animal_id: initial?.animal_id || '',
    box: initial?.box || '',
    responsavel_id: initial?.responsavel_id || '',
    prazo: initial?.prazo ? initial.prazo.slice(0, 16) : '',
    prioridade: initial?.prioridade || 'normal',
  });
  const [users, setUsers] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [saving, setSaving] = useState(false);

  // Filtrar animais pela box escolhida (para reduzir a lista e facilitar a escolha).
  // Se nenhuma box estiver selecionada, mostra todos os animais (sem saída).
  const animaisFiltrados = useMemo(() => {
    if (!f.box) return animais;
    return animais.filter(a => (a.box || '') === f.box);
  }, [animais, f.box]);

  useEffect(() => {
    (async () => {
      try {
        const [u, b, a] = await Promise.all([
          axios.get(`${API}/tarefas/utilizadores`),
          axios.get(`${API}/boxes/nomes`).catch(() => ({ data: [] })),
          axios.get(`${API}/animais`).catch(() => ({ data: [] })),
        ]);
        setUsers(u.data);
        setBoxes(b.data);
        setAnimais((a.data || []).filter(x => !x.saida));
      } catch (e) { console.error(e); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.titulo.trim()) { alert('Título obrigatório'); return; }
    if (!f.responsavel_id) { alert('Responsável obrigatório'); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        prazo: f.prazo ? new Date(f.prazo).toISOString() : null,
        animal_id: f.animal_id || null,
        box: f.box || null,
        descricao: f.descricao || null,
      };
      if (initial?.id) {
        await axios.put(`${API}/tarefas/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/tarefas`, payload);
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro a guardar tarefa');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <form onSubmit={submit} className="bg-card rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-3" data-testid="form-tarefa">
        <div className="flex items-center justify-between sticky top-0 bg-card pb-2">
          <h3 className="font-heading font-bold text-lg">{initial ? 'Editar tarefa' : 'Nova tarefa'}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Título *</label>
          <input data-testid="tarefa-titulo" required value={f.titulo} onChange={e => setF({ ...f, titulo: e.target.value })}
            placeholder="ex: Lavar a box 5"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium block mb-1">Tipo *</label>
            <select data-testid="tarefa-tipo" value={f.tipo} onChange={e => setF({ ...f, tipo: e.target.value })}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Prioridade</label>
            <select value={f.prioridade} onChange={e => setF({ ...f, prioridade: e.target.value })}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
              {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Responsável *</label>
          <select data-testid="tarefa-responsavel" required value={f.responsavel_id} onChange={e => setF({ ...f, responsavel_id: e.target.value })}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
            <option value="">— Selecionar —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.nome} ({u.role})</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium block mb-1">Box</label>
            <select value={f.box} onChange={e => {
              const novaBox = e.target.value;
              setF(prev => {
                // Se o animal selecionado não pertence à nova box, limpa-o.
                let novoAnimalId = prev.animal_id;
                if (novaBox && novoAnimalId) {
                  const a = animais.find(x => x.id === novoAnimalId);
                  if (a && (a.box || '') !== novaBox) novoAnimalId = '';
                }
                return { ...prev, box: novaBox, animal_id: novoAnimalId };
              });
            }}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
              <option value="">—</option>
              {boxes.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">
              Animal {f.box && <span className="text-muted-foreground font-normal">(filtrado por box)</span>}
            </label>
            <select value={f.animal_id} onChange={e => setF({ ...f, animal_id: e.target.value })}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
              <option value="">—</option>
              {animaisFiltrados.map(a => <option key={a.id} value={a.id}>{a.nome} {a.box ? `(${a.box})` : ''}</option>)}
            </select>
            {f.box && animaisFiltrados.length === 0 && (
              <p className="text-[11px] text-muted-foreground mt-1">Sem animais nesta box.</p>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Prazo</label>
          <input type="datetime-local" value={f.prazo} onChange={e => setF({ ...f, prazo: e.target.value })}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Descrição / instruções</label>
          <textarea value={f.descricao} onChange={e => setF({ ...f, descricao: e.target.value })} rows={3}
            placeholder="Detalhes para o responsável..."
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div className="flex gap-2 justify-end pt-2 sticky bottom-0 bg-card">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="tarefa-submit"
            className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving ? 'A guardar…' : (initial ? 'Guardar alterações' : 'Criar tarefa')}
          </button>
        </div>
      </form>
    </div>
  );
}

function DetalheTarefa({ tarefaId, onClose, onChanged, onEdit, canManage, isOp }) {
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadDetail, setReloadDetail] = useState(0);
  const [comentario, setComentario] = useState('');
  const [busy, setBusy] = useState(false);
  const [showConcluir, setShowConcluir] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/tarefas/${tarefaId}`);
        if (!cancelled) setT(r.data);
      } catch (e) {
        if (!cancelled) { alert(e.response?.data?.detail || 'Erro a carregar tarefa'); onClose(); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tarefaId, reloadDetail]);

  const load = () => setReloadDetail(k => k + 1);

  const iniciar = async () => {
    setBusy(true);
    try { await axios.post(`${API}/tarefas/${tarefaId}/iniciar`); load(); onChanged(); }
    catch (e) { alert(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };

  const enviarComentario = async () => {
    if (!comentario.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/tarefas/${tarefaId}/comentario`, { texto: comentario }); setComentario(''); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };

  const apagar = async () => {
    if (!confirm('Apagar esta tarefa? Esta ação não pode ser desfeita.')) return;
    setBusy(true);
    try { await axios.delete(`${API}/tarefas/${tarefaId}`); onClose(); onChanged(); }
    catch (e) { alert(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };

  const enviarFotoProva = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await compressImageToBase64(file, { maxDim: 1280, quality: 0.8 });
      await axios.post(`${API}/tarefas/${tarefaId}/foto-prova`, { foto: b64 });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a enviar foto');
    } finally { setBusy(false); }
  };

  if (loading || !t) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <Loader2 className="w-8 h-8 animate-spin text-white" />
    </div>
  );

  const estado = estadoCfg(t.estado);
  const podeAtuar = (canManage || (isOp && t.estado !== 'concluida' && t.estado !== 'cancelada'));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="detalhe-tarefa">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${tipoColor(t.tipo)}`}>{tipoLabel(t.tipo)}</span>
              <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${estado.color}`}>{estado.label}</span>
            </div>
            <h3 className="font-heading font-bold text-lg truncate">{t.titulo}</h3>
          </div>
          <button onClick={onClose} className="ml-2"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Responsável" value={t.responsavel_nome} />
            <Info label="Criado por" value={t.criador_nome} />
            {t.box && <Info label="Box" value={t.box} />}
            {t.animal_nome && <Info label="Animal" value={t.animal_nome} />}
            {t.prazo && <Info label="Prazo" value={fmtDateTime(t.prazo)} />}
            <Info label="Criada em" value={fmtDateTime(t.criado_em)} />
          </div>

          {t.descricao && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Descrição</p>
              <p className="text-sm bg-muted/40 rounded-xl p-3 whitespace-pre-wrap">{t.descricao}</p>
            </div>
          )}

          {t.fotos_prova?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Fotos de prova ({t.fotos_prova.length})</p>
              <div className="grid grid-cols-3 gap-2">
                {t.fotos_prova.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={`prova-${i}`} className="rounded-lg w-full h-20 object-cover border border-border" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {t.comentarios?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Comentários ({t.comentarios.length})</p>
              <div className="space-y-2">
                {t.comentarios.map((c, i) => (
                  <div key={i} className="bg-muted/40 rounded-xl p-2 text-sm">
                    <div className="text-[10px] text-muted-foreground mb-0.5">{c.autor_nome} • {fmtDateTime(c.timestamp)}{c.tipo === 'conclusao' ? ' • Conclusão' : ''}</div>
                    <div className="whitespace-pre-wrap">{c.texto}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {podeAtuar && t.estado !== 'concluida' && t.estado !== 'cancelada' && (
            <>
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {t.estado === 'pendente' && (
                    <button data-testid="btn-iniciar" disabled={busy} onClick={iniciar}
                      className="flex items-center gap-1.5 bg-blue-600 text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50">
                      <Play className="w-4 h-4" /> Iniciar
                    </button>
                  )}
                  <button data-testid="btn-concluir" disabled={busy} onClick={() => setShowConcluir(true)}
                    className="flex items-center gap-1.5 bg-green-600 text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50">
                    <CheckCircle2 className="w-4 h-4" /> Marcar como concluída
                  </button>
                  <label className="flex items-center gap-1.5 bg-muted text-foreground rounded-full px-4 py-2 text-sm font-medium cursor-pointer">
                    <Camera className="w-4 h-4" /> Adicionar foto
                    <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => enviarFotoProva(e.target.files[0])} />
                  </label>
                </div>

                <div className="flex gap-2">
                  <input value={comentario} onChange={e => setComentario(e.target.value)} placeholder="Comentar..."
                    className="flex-1 rounded-full border border-border bg-background px-3 py-2 text-sm" />
                  <button data-testid="btn-comentar" disabled={busy || !comentario.trim()} onClick={enviarComentario}
                    className="bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm disabled:opacity-50">
                    Enviar
                  </button>
                </div>
              </div>
            </>
          )}

          {canManage && (
            <div className="border-t border-border pt-3 flex gap-2 justify-end">
              <button onClick={() => onEdit(t)} className="flex items-center gap-1.5 bg-muted text-foreground rounded-full px-3 py-1.5 text-xs font-medium">
                <Edit3 className="w-3.5 h-3.5" /> Editar
              </button>
              <button onClick={apagar} className="flex items-center gap-1.5 bg-red-100 text-red-700 rounded-full px-3 py-1.5 text-xs font-medium">
                <Trash2 className="w-3.5 h-3.5" /> Apagar
              </button>
            </div>
          )}
        </div>
      </div>

      {showConcluir && (
        <ConcluirModal tarefaId={tarefaId} onClose={() => setShowConcluir(false)} onDone={() => { setShowConcluir(false); load(); onChanged(); }} />
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm truncate">{value || '—'}</p>
    </div>
  );
}

function ConcluirModal({ tarefaId, onClose, onDone }) {
  const [coment, setComent] = useState('');
  const [fotos, setFotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const onFilesAdd = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      const b64s = await Promise.all(list.map(f => compressImageToBase64(f, { maxDim: 1280, quality: 0.8 })));
      setFotos(prev => [...prev, ...b64s]);
    } catch (e) { alert('Erro a processar imagens'); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      // Upload each photo first
      const urls = [];
      for (const b64 of fotos) {
        const r = await axios.post(`${API}/tarefas/${tarefaId}/foto-prova`, { foto: b64 });
        if (r.data?.url) urls.push(r.data.url);
      }
      await axios.post(`${API}/tarefas/${tarefaId}/concluir`, {
        comentario: coment || null,
      });
      onDone();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a concluir tarefa');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-card rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl w-full sm:max-w-md p-5 space-y-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-600" /> Concluir tarefa</h3>
        <div>
          <label className="text-xs font-medium block mb-1">Comentário final (opcional)</label>
          <textarea rows={3} value={coment} onChange={e => setComent(e.target.value)}
            placeholder="ex: Tudo limpo, sem incidentes"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Fotos de prova (opcional)</label>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-4 cursor-pointer hover:bg-muted/40 transition-colors">
            <Camera className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Tirar/escolher foto</span>
            <input type="file" accept="image/*" capture="environment" multiple hidden onChange={e => onFilesAdd(e.target.files)} />
          </label>
          {fotos.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {fotos.map((b64, i) => (
                <div key={i} className="relative">
                  <img src={`data:image/jpeg;base64,${b64}`} alt={`f-${i}`} className="rounded-lg w-full h-16 object-cover" />
                  <button type="button" onClick={() => setFotos(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
          <button type="button" disabled={saving} onClick={submit} data-testid="confirmar-concluir"
            className="px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium disabled:opacity-50">
            {saving ? 'A guardar…' : 'Concluir'}
          </button>
        </div>
      </div>
    </div>
  );
}


function PreferenciasModal({ onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/preferencias`);
        if (!cancelled) setPrefs(r.data);
      } catch (e) {
        if (!cancelled) { alert('Erro a carregar preferências'); onClose(); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [onClose]);

  if (loading || !prefs) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const qh = prefs.quiet_hours || { enabled: false, start: '22:00', end: '07:00', allow_high_priority: true };
  const setQh = (patch) => setPrefs({ ...prefs, quiet_hours: { ...qh, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/preferencias`, { quiet_hours: qh });
      setSaving(false);
      onClose();
    } catch (e) {
      setSaving(false);
      alert(e.response?.data?.detail || 'Erro a guardar');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-card rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="preferencias-modal">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Moon className="w-5 h-5 text-primary" /> Não-perturbar
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            Durante este horário, não receberás notificações push. As tarefas continuam a aparecer na app normalmente.
          </p>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium">Ativar horário silencioso</p>
              <p className="text-xs text-muted-foreground">Bloqueia pushes durante a janela definida</p>
            </div>
            <input type="checkbox" data-testid="qh-enabled" checked={qh.enabled} onChange={e => setQh({ enabled: e.target.checked })}
              className="w-5 h-5 accent-primary" />
          </label>

          <div className={`grid grid-cols-2 gap-3 transition-opacity ${qh.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
            <div>
              <label className="text-xs font-medium block mb-1">De (início)</label>
              <input type="time" data-testid="qh-start" value={qh.start} onChange={e => setQh({ start: e.target.value })}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Até (fim)</label>
              <input type="time" data-testid="qh-end" value={qh.end} onChange={e => setQh({ end: e.target.value })}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </div>

          <label className={`flex items-center justify-between gap-3 cursor-pointer transition-opacity ${qh.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
            <div>
              <p className="text-sm font-medium flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> Permitir tarefas urgentes
              </p>
              <p className="text-xs text-muted-foreground">Notificações de prioridade alta passam o filtro</p>
            </div>
            <input type="checkbox" data-testid="qh-allow-high" checked={qh.allow_high_priority} onChange={e => setQh({ allow_high_priority: e.target.checked })}
              className="w-5 h-5 accent-primary" />
          </label>

          <div className="bg-muted/40 rounded-xl p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Como funciona?</p>
            <p>Se hoje for {new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })} e o horário silencioso estiver ativo entre <b>{qh.start}</b> e <b>{qh.end}</b>, novas tarefas chegam à app mas <b>não tocam o telemóvel</b>.</p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="qh-save"
            className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
